import { useCallback } from "react";
import { CoachComponents, PermissionLevel, WebComponents, WebSettings } from "@/interfaces";
import { useQuery } from "@tanstack/react-query";
import { useSocialCoachSession } from "./useSocialCoachSession";
import { AccountSettingServices } from "@/services/bedrock";
import { find } from "lodash";

export const useAccountSettings = (defaultCoachId: number | string | undefined = undefined) => {
  const { coachId, isAssistantCoach, session } = useSocialCoachSession();
  const effectiveCoachId = Number(defaultCoachId || coachId);

  const queryOptions = {
    meta: { persist: true },
    enabled: !!effectiveCoachId,
    staleTime: 1000 * 60, // 1 minute
    gcTime: 1000 * 60 * 60 * 13, // 13 hours
  };

  const { data: accountSettings = [], isLoading: accountSettingsLoading } = useQuery({
    queryKey: ["accountSettings"],
    queryFn: async () => {
      const response = await AccountSettingServices.getAccountSettings();
      return response.content;
    },
    ...queryOptions,
  });

  const {
    data: accountPermissions = [],
    isLoading: accountPermissionsLoading,
    isPending: accountPermissionsPending,
  } = useQuery({
    queryKey: ["accountPermissions", effectiveCoachId],
    queryFn: async () => {
      const response = await AccountSettingServices.getAccountSettingPermissions({ coachId: effectiveCoachId });
      return response.content;
    },
    ...queryOptions,
  });

  const hasAccountSetting = useCallback(
    (settingId: WebSettings, fallbackValueIfUnloaded: boolean = false) => {
      const accountPermission = accountPermissions.find(p => p.accountSettingId === settingId);
      if (accountPermission) return accountPermission.value;

      if (!accountPermission && accountPermissionsPending) {
        return fallbackValueIfUnloaded;
      }

      const accountSetting = accountSettings.find(s => s.id === settingId);
      if (accountSetting) return accountSetting.defaultValue;

      return false;
    },
    [accountPermissions, accountSettings, accountPermissionsPending]
  );

  const hasAccessToCoachComponent = useCallback(
    (componentId: WebComponents, fallbackValueIfUnloaded: boolean = false) => {
      const assistantPermission = CoachComponents[componentId];
      if (!assistantPermission) return false;
      if (assistantPermission.webSetting)
        return hasAccountSetting(assistantPermission.webSetting, fallbackValueIfUnloaded);
      return true;
    },
    [hasAccountSetting]
  );

  const hasCoachComponentPermission = useCallback(
    (
      componentId: WebComponents,
      permissionLevel: "READ" | "WRITE" = "READ",
      fallbackValueIfUnloaded: boolean = false
    ): boolean => {
      if (!coachId) return false;

      if (!hasAccessToCoachComponent(componentId, fallbackValueIfUnloaded)) return false;

      if (isAssistantCoach) {
        const assistantPermission = find(session?.userPermissions, { componentId });
        return assistantPermission
          ? PermissionLevel[assistantPermission.level] >= PermissionLevel[permissionLevel]
          : false;
      } else {
        return true;
      }
    },
    [isAssistantCoach, coachId, session?.userPermissions, hasAccessToCoachComponent]
  );

  const hasWritePermission = useCallback(
    (componentId: WebComponents | WebComponents[], fallbackValueIfUnloaded: boolean = false) => {
      const componentIds = Array.isArray(componentId) ? componentId : [componentId];
      return componentIds.some(id => hasCoachComponentPermission(id, "WRITE", fallbackValueIfUnloaded));
    },
    [hasCoachComponentPermission]
  );

  const hasReadPermission = useCallback(
    (componentId: WebComponents | WebComponents[], fallbackValueIfUnloaded: boolean = false) => {
      const componentIds = Array.isArray(componentId) ? componentId : [componentId];
      return componentIds.some(id => hasCoachComponentPermission(id, "READ", fallbackValueIfUnloaded));
    },
    [hasCoachComponentPermission]
  );

  return {
    accountSettingsLoading,
    accountPermissionsLoading,
    hasAccountSetting,
    hasWritePermission,
    hasReadPermission,
    hasAccessToCoachComponent,
  };
};
